/**
 * The Internal Audits verbs (13-32, give-first, D-13-L-13): schedule, edit, start, complete, approve and the
 * follow-up rows, as one pure builder, so FIELDSET §1.8's audit row and the carry-in guard are proven without
 * SharePoint.
 *
 * `buildAuditVerb(verb, row, actor, input, now, ctx)` → `{ ok: true, patch, siblings, warnings }` or
 * `{ ok: false, refusals, warnings }`, shaped like 13-20's `buildRecordVerb`, and drafted through its audit contract:
 * the PATCH holds only the columns that change, one `StateChange` sibling draft per changed column
 * (`draftStateChanges`), `ActorUPN` on every row, and one `[a:<id>]` tag per save (`finaliseDrafts`). A schedule
 * (a new row) is the create payload, its drafts carry no `ParentRef` yet and are titled `schedule: <column>` (the
 * service composes `AUD-<id>` from the id Graph returns, as the lodge does).
 *
 * The rules (FIELDSET §2.6, §3.6, §1.8; the plan):
 * - **Oversight only** (§2.6's author column); every other person is refused `not-oversight`.
 * - **`CompletedOn` is written only by complete** (→ `Completed`), and **`ApprovedOn` + `ApprovedByUPN` only by
 *   approve** (`Completed` → `Approved`). `CentroRef` by no verb.
 * - **No self-approval (D-13-32-1):** approve is refused when the actor is the row's `AuditorUPN`, and refused
 *   `auditor-unknown` when no auditor is recorded (the D-13-00-5 / D-13-L-19 safeguard, failing closed).
 * - **The carry-in guard:** until 1 Dec 2026 (Melbourne), a row with a `CentroRef` (AUD-1…11) may change only on
 *   `AuditKind`, `AuditorIndependent` and a person column its carry-in trail row says was defaulted to the runner;
 *   anything else is refused `carried-until-cutover`, because the 30 Nov re-run would put Centro's value back.
 *   Follow-up rows are allowed (rows are never overwritten, D-13-32-5).
 * - Dates (§1.4): `DueOn`, `CompletedOn` and `ApprovedOn` are `dateOnly` days sent as the intended Melbourne day,
 *   `YYYY-MM-DD`, never through a browser `Date`; a stored value (Pacific midnight) is read by its Melbourne day.
 *
 * Pure: no Graph, no React, no `Date.now()`; `now` and `input.auditId` come from the caller.
 */

import {
  AUDIT_REQUIRED,
  AUDIT_ROW_KINDS,
  AUDIT_STATUS,
  AUDIT_VERB_INPUTS,
  AUDIT_VERB_STAMPS,
  CARRY_IN,
  OPEN_AUDIT_STATUSES,
  auditChoices,
  auditField,
  isCarried,
  type AuditFieldSpec,
  type AuditVerb,
} from '../config/fieldset/internalAudits';
import { LOG_KINDS } from '../config/fieldset/siblings';
import type { Actor } from '../types/actionRegister';
import type { RecordFieldValue, RecordPatch, Refusal, SiblingDraft } from '../types/records';
import {
  checkChoice,
  checkUpn,
  cleanText,
  dayExisting,
  dayNew,
  formatDay,
  melbourneDayOf,
  melbourneWallToInstant,
  notInFuture,
  toInstant,
  todayMelbourne,
} from './fieldValues';
import type { WallTime } from './lodgeBuilder';
import { cleanNoteText, draftStateChanges, finaliseDrafts, isBlank } from './recordVerbs';
import type { ColumnChange, DraftContext, PendingDraft } from './recordVerbs';

export type { AuditVerb };

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** One Internal Audits item as the dashboard reads it (`fields` by SharePoint internal name). */
export interface AuditRow {
  id: number;
  /** `AUD-<SP Id>`, or `AUD-P<n>` for a practice audit. */
  ref: string;
  /** The item eTag, sent as `If-Match` (D-13-L-4). */
  eTag: string;
  fields: Record<string, unknown>;
}

/** Why a verb may not run at all on this row for this person. */
export type AuditAuthorityReason =
  | 'invalid-actor'
  | 'not-oversight'
  | 'not-new'
  | 'no-row'
  | 'wrong-status'
  | 'carried-until-cutover'
  | 'self-approval'
  | 'auditor-unknown';

export type AuditAuthorityResult = { ok: true } | { ok: false; reason: AuditAuthorityReason };

const CUTOVER_LABEL = formatDay(CARRY_IN.cutoverDay);

/** Each reason in words (shown at the panel; Today's calm language, never "permission denied"). */
export const AUDIT_AUTHORITY_TEXT: Readonly<Record<AuditAuthorityReason, string>> = {
  'invalid-actor': 'The signed-in account has no usable email address.',
  'not-oversight': 'Internal audits are scheduled, completed and approved by Oversight.',
  'not-new': 'This audit is already scheduled. Change its details instead.',
  'no-row': 'Open the audit first.',
  'wrong-status': 'That step is not open at this audit’s stage.',
  'carried-until-cutover': `This audit was carried in from Centro. Until ${CUTOVER_LABEL}, only its audit kind, the auditor’s independence and a person the carry-in filled in for Centro can change here: the 30 Nov re-run brings Centro’s values back over anything else.`,
  'self-approval': 'Someone else approves this audit, because you are its auditor.',
  'auditor-unknown': 'No auditor is recorded, so nobody can show that someone else approves it. Record the auditor first.',
};

/** One follow-up row (§3.4.1): a `Review` (the follow-up verdict) or an `AuditMeeting`. `at`: when it happened. */
export interface AuditRowInput {
  logKind: string;
  text: string;
  at?: WallTime;
}

/** What the person saves. `set` by internal name; `null` or a blank clears. `auditId` is the `[a:<id>]` tag. */
export interface AuditVerbInput {
  set?: Readonly<Record<string, unknown>>;
  rows?: readonly AuditRowInput[];
  auditId: string;
}

/** Facts the caller read from the trail: which person columns the carry-in defaulted (`carriedDefaults`). */
export interface AuditVerbContext {
  carriedDefaults?: ReadonlySet<string>;
}

export type AuditVerbBuild =
  | { ok: true; patch: RecordPatch; siblings: SiblingDraft[]; warnings: Refusal[] }
  | { ok: false; refusals: Refusal[]; warnings: Refusal[] };

/** The verb at the start of each sibling Note ("Completion by …"). */
export const AUDIT_VERB_LABELS: Readonly<Record<AuditVerb, string>> = {
  schedule: 'Schedule',
  edit: 'Schedule change',
  start: 'Start',
  complete: 'Completion',
  approve: 'Approval',
  followUp: 'Follow-up',
};

/** The verb as a timeline heading (`lib/recordTimeline`). */
export const AUDIT_VERB_WORDS: Readonly<Record<AuditVerb, string>> = {
  schedule: 'Audit scheduled',
  edit: 'Schedule changed',
  start: 'Audit started',
  complete: 'Audit completed',
  approve: 'Audit approved',
  followUp: 'Follow-up',
};

// ---------------------------------------------------------------------------
// Who, when, and the carry-in guard
// ---------------------------------------------------------------------------

const lower = (v: unknown) => (typeof v === 'string' ? v.trim().toLowerCase() : '');

function isOversight(actor: Pick<Actor, 'roles'> | null | undefined): boolean {
  return Array.isArray(actor?.roles) && actor.roles.includes('Oversight');
}

/** Before the cutover day in Melbourne: a carried row is still the carry-in's. */
function beforeCutover(now: Date): boolean {
  return todayMelbourne(now) < CARRY_IN.cutoverDay;
}

function guarded(row: AuditRow | null, now: Date): boolean {
  return row !== null && isCarried(row.fields) && beforeCutover(now);
}

function statusOf(row: AuditRow): string {
  return typeof row.fields.AuditStatus === 'string' ? row.fields.AuditStatus : '';
}

const START_FROM: readonly string[] = [AUDIT_STATUS.awaiting, AUDIT_STATUS.overdue];

/**
 * May this person run this verb on this row now? Oversight only; then the carry-in guard; then the stage; then the
 * no-self-approval rule. `edit` on a guarded row is allowed here, and each column is checked by the builder.
 */
export function canDoAuditVerb(verb: AuditVerb, row: AuditRow | null, actor: Pick<Actor, 'upn' | 'roles'>, now: Date): AuditAuthorityResult {
  if (!checkUpn(actor?.upn as string).ok) return { ok: false, reason: 'invalid-actor' };
  if (!isOversight(actor)) return { ok: false, reason: 'not-oversight' };
  if (verb === 'schedule') return row === null ? { ok: true } : { ok: false, reason: 'not-new' };
  if (row === null) return { ok: false, reason: 'no-row' };
  const status = statusOf(row);
  const carried = guarded(row, now);
  switch (verb) {
    case 'edit':
      return carried || OPEN_AUDIT_STATUSES.includes(status) ? { ok: true } : { ok: false, reason: 'wrong-status' };
    case 'start':
      if (carried) return { ok: false, reason: 'carried-until-cutover' };
      return START_FROM.includes(status) ? { ok: true } : { ok: false, reason: 'wrong-status' };
    case 'complete':
      if (carried) return { ok: false, reason: 'carried-until-cutover' };
      return OPEN_AUDIT_STATUSES.includes(status) ? { ok: true } : { ok: false, reason: 'wrong-status' };
    case 'approve': {
      if (carried) return { ok: false, reason: 'carried-until-cutover' };
      if (status !== AUDIT_STATUS.completed) return { ok: false, reason: 'wrong-status' };
      const auditor = lower(row.fields.AuditorUPN);
      if (!auditor) return { ok: false, reason: 'auditor-unknown' };
      return auditor === lower(actor.upn) ? { ok: false, reason: 'self-approval' } : { ok: true };
    }
    case 'followUp':
      return { ok: true };
    default:
      return { ok: false, reason: 'wrong-status' };
  }
}

/**
 * On a guarded carried row, the columns that may change (the two never-written ones, then each defaulted person
 * column, in `CARRY_IN` order); `null` when the row is not carried or the guard has lifted.
 */
export function carriedEditableColumns(row: AuditRow, now: Date, ctx: AuditVerbContext = {}): string[] | null {
  if (!guarded(row, now)) return null;
  const defaults = ctx.carriedDefaults ?? new Set<string>();
  return [...CARRY_IN.editable, ...CARRY_IN.defaultable.filter((c) => defaults.has(c))];
}

/** The steps the panel offers on this row, in order (the stage and the role; self-approval is said at the step). */
export function nextAuditVerbs(row: AuditRow, actor: Pick<Actor, 'upn' | 'roles'>, now: Date): AuditVerb[] {
  const verbs: AuditVerb[] = ['edit', 'start', 'complete', 'approve', 'followUp'];
  return verbs.filter((v) => {
    const r = canDoAuditVerb(v, row, actor, now);
    return r.ok || (v === 'approve' && (r.reason === 'self-approval' || r.reason === 'auditor-unknown'));
  });
}

// ---------------------------------------------------------------------------
// The list's four tabs (D-13-15-4: overdue is amber)
// ---------------------------------------------------------------------------

export type AuditBucket = 'scheduled' | 'overdue' | 'awaiting-approval' | 'approved';

/** Overdue: not Completed or Approved, and `DueOn` (its Melbourne day) before Melbourne today. */
export function isOverdue(row: AuditRow, now: Date): boolean {
  const status = statusOf(row);
  if (status === AUDIT_STATUS.completed || status === AUDIT_STATUS.approved) return false;
  const due = melbourneDayOf(typeof row.fields.DueOn === 'string' ? row.fields.DueOn : null);
  return due !== null && due < todayMelbourne(now);
}

export function auditBucket(row: AuditRow, now: Date): AuditBucket {
  const status = statusOf(row);
  if (status === AUDIT_STATUS.approved) return 'approved';
  if (status === AUDIT_STATUS.completed) return 'awaiting-approval';
  return isOverdue(row, now) ? 'overdue' : 'scheduled';
}

// ---------------------------------------------------------------------------
// Values by kind
// ---------------------------------------------------------------------------

type Cleaned = { kind: 'blank' } | { kind: 'value'; value: RecordFieldValue } | { kind: 'refused'; code: string; message: string };

const BLANK: Cleaned = { kind: 'blank' };
const refused = (code: string, message: string): Cleaned => ({ kind: 'refused', code, message });
const LINK = /^https?:\/\/[^\s]+$/i;

function cleanValue(spec: AuditFieldSpec, raw: unknown): Cleaned {
  if (raw === null || raw === undefined) return BLANK;
  switch (spec.kind) {
    case 'text':
    case 'textarea': {
      const r = cleanText(raw as string, spec.cap ?? 255);
      if (!r.ok) return r.code === 'too-long' ? refused('too-long', `${spec.label} is ${r.length} characters; the limit is ${r.cap}.`) : refused('not-text', `${spec.label} must be text.`);
      return r.value === '' ? BLANK : { kind: 'value', value: r.value };
    }
    case 'link': {
      const r = cleanText(raw as string, spec.cap ?? 255);
      if (!r.ok) {
        return r.code === 'too-long'
          ? refused('link-too-long', `${spec.label} is ${r.length} characters; a link here can be at most ${r.cap}. Link the folder, not a long file path.`)
          : refused('not-text', `${spec.label} must be text.`);
      }
      if (r.value === '') return BLANK;
      return LINK.test(r.value) ? { kind: 'value', value: r.value } : refused('not-a-link', `${spec.label}: paste the web address (it starts https://).`);
    }
    case 'choice': {
      if (isBlank(raw)) return BLANK;
      const r = checkChoice(raw as string, auditChoices(spec.name));
      return r.ok ? { kind: 'value', value: r.value } : refused(r.code, `${spec.label}: choose one of the listed options.`);
    }
    case 'upn': {
      if (isBlank(raw)) return BLANK;
      const r = checkUpn(raw as string);
      if (r.ok) return { kind: 'value', value: r.value };
      return r.code === 'guest-upn'
        ? refused(r.code, `${spec.label}: a guest account can't be recorded here; use their Harmony account.`)
        : refused(r.code, `${spec.label}: enter one email address, not a name.`);
    }
    case 'day-new': {
      if (isBlank(raw)) return BLANK;
      const r = dayNew(String(raw).trim());
      return r.ok ? { kind: 'value', value: r.value } : refused(r.code, `${spec.label}: enter a real date as YYYY-MM-DD.`);
    }
    case 'number': {
      if (isBlank(raw)) return BLANK;
      const text = typeof raw === 'number' ? String(raw) : String(raw).trim();
      if (!/^-?\d+$/.test(text)) return refused('not-a-score', `${spec.label}: enter a whole number from 0 to 100.`);
      const n = Number(text);
      return n < 0 || n > 100 ? refused('score-out-of-range', `${spec.label}: enter a whole number from 0 to 100.`) : { kind: 'value', value: n };
    }
    case 'attachments':
      return refused('not-writable', `${spec.label}: attach them on the SharePoint item.`);
    default:
      throw new Error(`auditVerbs: ${spec.name} has kind ${String(spec.kind)}, which no verb writes`);
  }
}

/** Old and new are the same once normalised by kind: days by their Melbourne day, UPNs ignoring case, text trimmed. */
function sameValue(spec: AuditFieldSpec, old: unknown, next: RecordFieldValue): boolean {
  const oldBlank = isBlank(old);
  const nextBlank = isBlank(next);
  if (oldBlank || nextBlank) return oldBlank && nextBlank;
  switch (spec.kind) {
    case 'day-new': {
      const a = melbourneDayOf(String(old));
      const b = melbourneDayOf(String(next));
      return a !== null && b !== null ? a === b : String(old) === String(next);
    }
    case 'upn':
      return lower(old) === lower(next);
    case 'number':
      return Number(old) === Number(next);
    case 'text':
    case 'textarea':
    case 'link':
      return String(old).trim() === String(next).trim();
    default:
      return String(old) === String(next);
  }
}

const stored = (v: unknown) => (isBlank(v) ? '' : String(v));

// ---------------------------------------------------------------------------
// Rows (§3.4.1)
// ---------------------------------------------------------------------------

type RowBuild = { ok: true; draft: PendingDraft } | { ok: false; code: string; message: string };

function buildRow(row: AuditRow, input: AuditRowInput, actor: Pick<Actor, 'lookupId'>, actorUpn: string, now: Date): RowBuild {
  const kind = typeof input?.logKind === 'string' ? input.logKind.trim() : '';
  if (kind === '') return { ok: false, code: 'logkind-required', message: 'Choose what kind of entry this is.' };
  if (!LOG_KINDS.includes(kind) || !AUDIT_ROW_KINDS.includes(kind)) {
    return { ok: false, code: 'logkind-not-allowed', message: `A ${kind} entry isn't recorded on an internal audit.` };
  }
  const text = cleanNoteText(input.text ?? null, 'The entry');
  if (!text.ok) return { ok: false, code: text.code, message: text.message };
  if (text.value === '') return { ok: false, code: 'required', message: 'Write what happened.' };

  let at = toInstant(now);
  if (input.at !== undefined) {
    const day = typeof input.at?.day === 'string' ? input.at.day : '';
    const time = typeof input.at?.time === 'string' ? input.at.time.trim() : '';
    let when: string | null = null;
    if (time === '') {
      const d = dayExisting(day);
      if (!d.ok) return { ok: false, code: d.code, message: 'When it happened: enter a real date as YYYY-MM-DD.' };
      when = d.value;
    } else {
      const w = melbourneWallToInstant(day, time);
      if (!w.ok) return { ok: false, code: w.code, message: 'When it happened: check the date and the time.' };
      when = w.value;
    }
    if (!notInFuture(when, now).ok) return { ok: false, code: 'in-future', message: "When it happened can't be in the future." };
    at = when;
  }

  const draft: PendingDraft = {
    Title: `followUp ${row.ref}: ${kind}`,
    ParentRef: row.ref,
    EntryType: 'Note',
    LogKind: kind,
    EntryTimestamp: at,
    ActorUPN: actorUpn,
    ...(actor.lookupId ? { ActorLookupId: actor.lookupId } : {}),
    noteHead: `${text.value}\n${AUDIT_VERB_LABELS.followUp} by ${actorUpn}.`,
  };
  return { ok: true, draft };
}

// ---------------------------------------------------------------------------
// The builder
// ---------------------------------------------------------------------------

/** Build one audit verb save, or every refusal in one pass. See the module comment for the rules. */
export function buildAuditVerb(
  verb: AuditVerb,
  row: AuditRow | null,
  actor: Pick<Actor, 'upn' | 'roles' | 'lookupId'>,
  input: AuditVerbInput,
  now: Date,
  ctx: AuditVerbContext = {},
): AuditVerbBuild {
  if (!input || typeof input.auditId !== 'string' || input.auditId.trim() === '') {
    throw new Error('buildAuditVerb: input.auditId is required (the [a:<id>] tag)');
  }
  const warnings: Refusal[] = [];
  const gate = canDoAuditVerb(verb, row, actor, now);
  if (!gate.ok) return { ok: false, refusals: [{ code: gate.reason, message: AUDIT_AUTHORITY_TEXT[gate.reason] }], warnings };

  const refusals: Refusal[] = [];
  const refuse = (code: string, column: string | undefined, message: string) =>
    refusals.push(column === undefined ? { code, message } : { code, column, message });
  const actorUpn = (checkUpn(actor.upn).ok ? lower(actor.upn) : '') as string;
  const fields = row?.fields ?? {};

  // --- Which columns this save may take ------------------------------------------------------------------------
  const stamps = AUDIT_VERB_STAMPS[verb];
  const carriedOnly = row !== null ? carriedEditableColumns(row, now, ctx) : null;
  const inputs = new Set<string>(AUDIT_VERB_INPUTS[verb]);
  // D-13-32-4: on a guarded carried row, `edit` may also correct a defaulted person column outside the schedule set.
  if (verb === 'edit' && carriedOnly) for (const c of carriedOnly) inputs.add(c);

  // --- Pass 1: every key in set ----------------------------------------------------------------------------------
  const given = new Map<string, RecordFieldValue>();
  const bad = new Set<string>();
  for (const [key, raw] of Object.entries(input.set ?? {})) {
    if (raw === undefined) continue;
    const spec = auditField(key);
    if (key in stamps) {
      refuse('stamped-column', key, `${spec?.label ?? key} is set when you save; it can't be entered.`);
      continue;
    }
    if (!spec || !inputs.has(key)) {
      refuse('not-this-verb', key, `${spec?.label ?? key} isn't recorded at this step.`);
      continue;
    }
    if (carriedOnly && !carriedOnly.includes(key)) {
      // Only refused when it would change something: a form may send the whole schedule back unchanged.
      const probe = cleanValue(spec, raw);
      const value = probe.kind === 'value' ? probe.value : null;
      if (probe.kind === 'refused' || !sameValue(spec, fields[key], value)) {
        refuse('carried-until-cutover', key, `${spec.label}: ${AUDIT_AUTHORITY_TEXT['carried-until-cutover']}`);
        bad.add(key);
      }
      continue;
    }
    const r = cleanValue(spec, raw);
    if (r.kind === 'refused') {
      refuse(r.code, key, r.message);
      bad.add(key);
      continue;
    }
    if (r.kind === 'value' && spec.kind === 'day-new' && key === 'CompletedOn' && !notInFuture(String(r.value), now).ok) {
      refuse('in-future', key, `${spec.label} can't be in the future.`);
      bad.add(key);
      continue;
    }
    given.set(key, r.kind === 'value' ? r.value : null);
  }

  // --- Pass 2: stamps, then the diff ---------------------------------------------------------------------------
  const proposed: Record<string, RecordFieldValue> = {};
  for (const [k, v] of given) proposed[k] = v;
  for (const [column, source] of Object.entries(stamps)) {
    proposed[column] = source === 'today' ? todayMelbourne(now) : source === 'actor' ? actorUpn : source;
  }
  const patch: RecordPatch = {};
  const changes: ColumnChange[] = [];
  for (const [column, next] of Object.entries(proposed)) {
    const spec = auditField(column) as AuditFieldSpec;
    if (row !== null && sameValue(spec, fields[column], next)) continue;
    if (row === null && isBlank(next)) continue; // a new row: a blank is left out, never "" or a default
    const cleared = isBlank(next);
    patch[column] = cleared ? null : next;
    changes.push({ column, oldValue: stored(fields[column]), newValue: cleared ? '' : String(next), cleared });
  }

  // --- Pass 3: required on the record as it would be after the save --------------------------------------------
  const merged = (c: string): unknown => (c in patch ? patch[c] : fields[c]);
  if (verb === 'edit') {
    for (const column of AUDIT_REQUIRED.edit) {
      if (!bad.has(column) && column in patch && isBlank(patch[column])) refuse('required', column, `${auditField(column)?.label ?? column} can't be cleared.`);
    }
  } else {
    for (const column of AUDIT_REQUIRED[verb]) {
      if (!bad.has(column) && isBlank(merged(column))) refuse('required', column, `${auditField(column)?.label ?? column} is required.`);
    }
  }

  // --- Rows (follow-up only) -----------------------------------------------------------------------------------
  const rowDrafts: PendingDraft[] = [];
  if ((input.rows ?? []).length > 0 && verb !== 'followUp') refuse('not-this-verb', 'rows', 'Follow-up entries are added on their own.');
  if (verb === 'followUp' && row !== null) {
    (input.rows ?? []).forEach((r, i) => {
      const b = buildRow(row, r, actor, actorUpn, now);
      if (b.ok) rowDrafts.push(b.draft);
      else refuse(b.code, `rows.${i}`, b.message);
    });
  }

  if (refusals.length > 0) return { ok: false, refusals, warnings };
  if (changes.length === 0 && rowDrafts.length === 0) {
    return { ok: false, refusals: [{ code: 'no-change', message: 'Nothing has changed, so there is nothing to save.' }], warnings };
  }

  // --- The drafts: one StateChange per changed column, then the rows; one tag per save --------------------------
  const draftCtx: DraftContext = {
    verb,
    label: AUDIT_VERB_LABELS[verb],
    actorUpn,
    ...(actor.lookupId ? { actorLookupId: actor.lookupId } : {}),
    now,
  };
  let stateChanges = draftStateChanges({ ref: row?.ref ?? '' }, changes, draftCtx);
  if (row === null) {
    // A new row: no ParentRef until Graph returns the id (the service composes AUD-<id>, as the lodge does).
    stateChanges = stateChanges.map((d) => {
      const out: PendingDraft = { ...d, Title: `schedule: ${d.FieldChanged}` };
      delete out.ParentRef;
      return out;
    });
  }
  const siblings = finaliseDrafts([...stateChanges, ...rowDrafts], input.auditId);
  return { ok: true, patch, siblings, warnings };
}
